import React, { useState, useEffect, useCallback } from 'react';
import { FiUpload, FiCloud } from 'react-icons/fi';
import { fetchUserPlans } from './api';
import { useConfirmAlert } from './hooks/useConfirmAlert';

export default function LoadDialog({
  show,
  onClose,
  loadJsonData,
  setLoadJsonData,
  onFileUpload,
  onLoadSubmit,
  onDatabaseLoad,
  user
}) {
  const [loadMode, setLoadMode] = useState(user ? 'database' : 'file'); // 'database' oder 'file'
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(false);
  const { alert: showAlert } = useConfirmAlert();

  const loadUserPlans = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchUserPlans(user.username);
      if (result.success) {
        setPlans(result.plans || []);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Pläne:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [user?.username]);

  useEffect(() => {
    if (show && loadMode === 'database' && user?.username) {
      loadUserPlans();
    }
  }, [show, loadMode, user?.username, loadUserPlans]);

  const handleDatabaseLoad = async () => {
    if (!selectedPlan) {
      await showAlert('Bitte wählen Sie einen Plan aus.', { title: "Plan auswählen" });
      return;
    }

    if (typeof onDatabaseLoad === 'function') {
      onDatabaseLoad(selectedPlan);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiUpload />
          Plan laden
        </h3>

        {/* Lade-Modus Auswahl */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '12px' }}>
            {user && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '2px solid #2196f3' }}>
                <input
                  type="radio"
                  value="database"
                  checked={loadMode === 'database'}
                  onChange={(e) => setLoadMode(e.target.value)}
                />
                <FiCloud />
                <div>
                  <strong>Aus "Meine Pläne" laden (empfohlen)</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>Ihre gespeicherten Cloud-Pläne</div>
                </div>
              </label>
            )}
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #ddd' }}>
              <input
                type="radio"
                value="file"
                checked={loadMode === 'file'}
                onChange={(e) => setLoadMode(e.target.value)}
              />
              <FiUpload />
              <div>
                Aus Datei laden
                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>Lokale .syllabus oder .json Datei</div>
              </div>
            </label>
          </div>
        </div>

        {/* Datenbank-Lade-Bereich */}
        {loadMode === 'database' && user && (
          <div>
            {loading ? (
              <p style={{ fontSize: '14px', color: '#666' }}>Lade Ihre Pläne...</p>
            ) : (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Plan auswählen:
                </label>
                {plans.length > 0 ? (
                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '16px'
                    }}
                  >
                    <option value="">-- Plan auswählen --</option>
                    {plans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} ({new Date(plan.updated_at || plan.created_at).toLocaleDateString('de-DE')})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p style={{ fontSize: '14px', color: '#666', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                    Keine Pläne gefunden. Erstellen Sie zuerst einen Plan und speichern Sie ihn in der Cloud.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Datei-Lade-Bereich */}
        {loadMode === 'file' && (
          <div>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>
              Wählen Sie eine lokale Datei zum Laden aus:
            </p>

            {/* Datei-Upload Bereich */}
            <div style={{
              border: '2px dashed #ccc',
              borderRadius: '6px',
              padding: '40px 20px',
              textAlign: 'center',
              marginBottom: '16px',
              backgroundColor: '#fafafa'
            }}>
              <input
                type="file"
                accept=".syllabus,.json"
                onChange={onFileUpload}
                style={{
                  marginBottom: '8px',
                  fontSize: '14px'
                }}
              />
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                Unterstützte Dateiformate: .syllabus, .json
              </p>
            </div>

            {/* Optional: Text-Eingabe für JSON */}
            {loadJsonData && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Dateiinhalt:
                </label>
                <textarea
                  value={loadJsonData}
                  onChange={(e) => setLoadJsonData(e.target.value)}
                  style={{
                    width: '100%',
                    height: '100px',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Informative Hinweise */}
        {!user && loadMode === 'database' && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            💡 Melden Sie sich an, um auf Ihre Cloud-Pläne zuzugreifen.
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            style={{
              padding: '8px 16px',
              border: '1px solid #ccc',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#4caf50',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onClick={loadMode === 'database' ? handleDatabaseLoad : onLoadSubmit}
            disabled={
              loadMode === 'database' 
                ? !selectedPlan 
                : (!loadJsonData || loadJsonData.trim() === '')
            }
          >
            {loadMode === 'database' ? <FiCloud /> : <FiUpload />}
            {loadMode === 'database' ? 'Aus Cloud laden' : 'Datei laden'}
          </button>
        </div>
      </div>
    </div>
  );
};


